import { zodResolver } from '@hookform/resolvers/zod';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { expiredPasswordSchema, loginSchema } from '@app/shared';
import type { LoginInput } from '@app/shared';
import { useBrandLook, useShellColours } from '../../app/BrandTheme';
import { APP_NAME } from '../../app/theme';
import { AppLockup } from '../../components/BrandLogo';
import { ApiError, api, errorMessage } from '../../lib/api';
import { useFeatureReleased } from '../../release';
import { useAuth } from './AuthProvider';
import { ForgotPasswordDialog } from './PasswordResetPages';

/**
 * Login Password Expiry Days (SC99): an expired password is replaced here before signing in. The
 * user name and the password just entered go with the new one; then the user is signed in.
 */
function ExpiredPasswordDialog({
  credentials,
  onRenewed,
  onClose,
}: {
  credentials: LoginInput;
  onRenewed: (input: LoginInput) => Promise<void>;
  onClose: () => void;
}) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = {
      loginId: credentials.loginId,
      currentPassword: credentials.password,
      newPassword,
      confirmPassword,
    };
    const parsed = expiredPasswordSchema.safeParse(body);
    if (!parsed.success) {
      const found: Record<string, string> = {};
      for (const issue of parsed.error.issues) found[String(issue.path[0])] ??= issue.message;
      setProblems(found);
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      await api.post('/auth/expired-password', body);
      await onRenewed({ ...credentials, password: newPassword });
    } catch (err) {
      setFailure(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={(e) => void submit(e)} noValidate aria-label="Choose a new password">
        <DialogTitle>Choose a new password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Your password has expired. Choose a new one to sign in.
          </DialogContentText>
          {failure ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {failure}
            </Alert>
          ) : null}
          <TextField
            label="New Password"
            type="password"
            autoComplete="new-password"
            autoFocus
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={Boolean(problems.newPassword)}
            helperText={problems.newPassword ?? 'At least 8 characters, with a letter and a digit'}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Confirm Password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={Boolean(problems.confirmPassword)}
            helperText={problems.confirmPassword}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy}>
            Save and Log In
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const fieldLabel = {
  fontSize: 12,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  mb: 0.5,
} as const;

export function LoginPage() {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  // The super instance (hq.212ez.com) has its own sign-in for super admins.
  const onHq = window.location.hostname.startsWith('hq.');

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Credentials held while the user decides whether to end their other session. */
  const [pending, setPending] = useState<LoginInput | null>(null);
  /** Credentials of a user whose password has expired, while they choose a new one. */
  const [expired, setExpired] = useState<LoginInput | null>(null);
  const [forgot, setForgot] = useState(false);
  const featureOn = useFeatureReleased();
  const colours = useShellColours();
  const brandLook = useBrandLook();
  const forgotOn = featureOn('forgot-password');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { loginId: '', password: '' },
  });

  if (status === 'signedIn') return <Navigate to={from} replace />;
  if (onHq) return <Navigate to="/hq" replace />;

  const attempt = async (input: LoginInput) => {
    setError(null);
    try {
      await login(input);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'SESSION_ACTIVE') setPending(input);
      else if (err instanceof ApiError && err.code === 'PASSWORD_EXPIRED') setExpired(input);
      else setError(errorMessage(err));
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        background: colours.loginBg,
      }}
    >
      <Paper elevation={4} sx={{ width: '100%', maxWidth: 400, p: 4 }}>
        {brandLook ? (
          <Typography variant="h1" sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <AppLockup height={96} />
          </Typography>
        ) : featureOn('brand-logo') ? (
          <Typography variant="h1" sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <AppLockup height={96} />
          </Typography>
        ) : (
          <Typography variant="h1" align="center" sx={{ color: colours.primary, mb: 3 }}>
            {APP_NAME}
          </Typography>
        )}
        <form onSubmit={handleSubmit(attempt)} noValidate>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}
          <Typography component="label" htmlFor="loginId" sx={fieldLabel} display="block">
            User name
          </Typography>
          <TextField
            id="loginId"
            autoComplete="username"
            autoFocus
            error={Boolean(errors.loginId)}
            helperText={errors.loginId?.message}
            sx={{ mb: 2 }}
            {...register('loginId')}
          />
          <Typography component="label" htmlFor="password" sx={fieldLabel} display="block">
            Password
          </Typography>
          <TextField
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
            sx={{ mb: 3 }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            {...register('password')}
          />
          <Button type="submit" variant="contained" size="large" fullWidth disabled={isSubmitting}>
            Log In
          </Button>
          {forgotOn ? (
            <Box sx={{ mt: 1.5, textAlign: 'center' }}>
              <Button size="small" onClick={() => setForgot(true)}>
                Forgot your password?
              </Button>
            </Box>
          ) : null}
        </form>
        {featureOn('mvno-signup') ? (
          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            New MVNO? <Link to="/signup">Create your company</Link>
          </Typography>
        ) : null}
        <Typography
          variant="caption"
          display="block"
          align="center"
          sx={{ mt: 3, color: 'text.secondary' }}
        >
          Access is restricted to authorised users.
        </Typography>
      </Paper>

      {forgot ? <ForgotPasswordDialog onClose={() => setForgot(false)} /> : null}
      {expired ? (
        <ExpiredPasswordDialog
          credentials={expired}
          onClose={() => setExpired(null)}
          onRenewed={async (input) => {
            setExpired(null);
            await attempt(input);
          }}
        />
      ) : null}
      <Dialog open={Boolean(pending)} onClose={() => setPending(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Already signed in</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This user is signed in on another device. Signing in here will end that session.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Cancel Login</Button>
          <Button
            variant="contained"
            onClick={() => {
              const input = pending;
              setPending(null);
              if (input) void attempt({ ...input, takeover: true });
            }}
          >
            Logout from Other Device
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
